// FieldOptions.js
// Singleton resolver for field option lists (preload-first).
// CONTRACT:
//   - Asset-first by FIELD NAME via AssetRegistry (assetKey === fieldName)
//   - Fallback to inline field.options (if provided)
//   - No dictionaries, no optionsSource, no optionsAssetKey
//
// SOURCE OF TRUTH:
//   - AssetEntries + S3 JSON (via AssetsRegistry.getAssetJson)
//   - This module only keeps a SHORT-LIVED per-build cache of normalized data.
//   - Caches are replaced atomically at the end of each buildFromFields(...) call.

import { getAssetEntryByKey, getAssetJson } from "../asset/AssetsRegistry";

/**
 * Return types (internal/cache):
 * - Flat: { type: "flat", options: [{name, value}] }
 * - Tree: { type: "tree", tree: Array<Node> }
 *   Node = { name: string, value: string|number|boolean, description?: string, children?: Node[] }
 */

/** Strict normalizer for flat arrays -> { type:"flat", options:[{name,value}] }.
 *  Accepts missing/empty `value` and defaults it to `name` (with warning). */
export function normalizeOptions(arr, where = "options") {
  if (!Array.isArray(arr)) {
    throw new Error(`[${where}] must be an array`);
  }

  const out = [];
  const fallbackIndexes = [];

  for (let i = 0; i < arr.length; i++) {
    const o = arr[i];
    if (!o || typeof o !== "object") {
      throw new Error(`[${where}] entry #${i} is not an object`);
    }
    if (typeof o.name !== "string" || o.name.length === 0) {
      throw new Error(`[${where}] entry #${i} missing valid 'name'`);
    }

    let value = o.value;

    // Treat null/undefined/empty-string as missing -> default to name (warn)
    if (
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.length === 0)
    ) {
      value = o.name;
      fallbackIndexes.push(i);
    }

    // Allow string | number | boolean only
    const t = typeof value;
    if (t !== "string" && t !== "number" && t !== "boolean") {
      throw new Error(
        `[${where}] entry #${i} 'value' must be a string, number, or boolean if provided`
      );
    }

    out.push({ name: o.name, value });
  }

  if (fallbackIndexes.length > 0) {
    console.warn(
      `[FieldOptions] ${where}: ${
        fallbackIndexes.length
      } option(s) missing/empty 'value' — defaulted to 'name'. ` +
        `Indexes: [${fallbackIndexes.join(", ")}].`
    );
  }

  return { type: "flat", options: out };
}

/** Normalizer for hierarchical arrays/objects -> { type:"tree", tree:[Node] } */
export function normalizeOptionTree(input, where = "options") {
  const hasChildrenKeys = (obj) =>
    obj &&
    typeof obj === "object" &&
    (Array.isArray(obj.topics) ||
      Array.isArray(obj.subtopics) ||
      Array.isArray(obj.children));

  const coerceChildren = (obj) =>
    obj?.topics || obj?.subtopics || obj?.children || [];

  const fallbackNodes = [];

  const buildNode = (obj, idxPath) => {
    if (!obj || typeof obj !== "object") {
      throw new Error(`[${where}] invalid node at ${idxPath}`);
    }
    if (typeof obj.name !== "string" || obj.name.length === 0) {
      throw new Error(`[${where}] node missing valid 'name' at ${idxPath}`);
    }

    let value = obj.value;
    let usedFallback = false;

    // Tree nodes require string value to override; otherwise default to name (warn)
    if (!(typeof value === "string" && value.length > 0)) {
      value = obj.name;
      usedFallback = true;
    }

    const node = { name: obj.name, value };
    if (obj.description && typeof obj.description === "string") {
      node.description = obj.description;
    }

    const kids = coerceChildren(obj);
    if (Array.isArray(kids) && kids.length) {
      node.children = kids.map((child, i) =>
        buildNode(child, `${idxPath}.${i}`)
      );
    }

    if (usedFallback) {
      fallbackNodes.push(idxPath);
    }

    return node;
  };

  // Root array determination
  let root;
  if (Array.isArray(input)) {
    root = input;
  } else if (input && typeof input === "object") {
    if (Array.isArray(input.options) || Array.isArray(input.items)) {
      // This is a FLAT container; let caller route to normalizeOptions
      throw new Error(
        `[${where}] flat container detected; use normalizeOptions()`
      );
    }
    if (
      Array.isArray(input.topics) ||
      Array.isArray(input.subtopics) ||
      Array.isArray(input.children)
    ) {
      root = input.topics || input.subtopics || input.children;
    } else {
      // Accept objects that are effectively a single node
      root =
        hasChildrenKeys(input) || typeof input.name === "string"
          ? [input]
          : null;
    }
  }

  if (!Array.isArray(root)) {
    throw new Error(
      `[${where}] hierarchical content must be an array (or object with 'topics'/'subtopics'/'children')`
    );
  }

  const tree = root.map((obj, i) => buildNode(obj, `root[${i}]`));

  if (fallbackNodes.length > 0) {
    console.warn(
      `[FieldOptions] ${where}: ${
        fallbackNodes.length
      } tree node(s) missing/empty 'value' — defaulted to 'name'. ` +
        `Nodes: ${fallbackNodes.join(", ")}.`
    );
  }

  return { type: "tree", tree };
}

/** True if input clearly describes a hierarchy (topics/subtopics/children with non-empty arrays) */
function looksHierarchical(input) {
  const hasKids = (n) =>
    !!n &&
    typeof n === "object" &&
    ((Array.isArray(n.topics) && n.topics.length > 0) ||
      (Array.isArray(n.subtopics) && n.subtopics.length > 0) ||
      (Array.isArray(n.children) && n.children.length > 0));

  if (Array.isArray(input)) {
    return input.some(hasKids);
  }
  if (input && typeof input === "object") {
    if (Array.isArray(input.topics) && input.topics.length > 0) return true;
    if (Array.isArray(input.subtopics) && input.subtopics.length > 0)
      return true;
    if (Array.isArray(input.children) && input.children.length > 0) return true;
    // single root object with nested kids also counts
    return hasKids(input);
  }
  return false;
}

/** Deep defensive clone so consumers can't mutate cached data */
function cloneOptionData(data) {
  if (!data || typeof data !== "object") return data;
  if (data.type === "flat") {
    return {
      type: "flat",
      options: data.options.map((o) => ({ name: o.name, value: o.value })),
    };
  }
  if (data.type === "tree") {
    const cloneNode = (n) => ({
      name: n.name,
      value: n.value,
      ...(n.description ? { description: n.description } : {}),
      ...(Array.isArray(n.children)
        ? { children: n.children.map(cloneNode) }
        : {}),
    });
    return { type: "tree", tree: data.tree.map(cloneNode) };
  }
  return JSON.parse(JSON.stringify(data));
}

/** Parse any asset content into our tagged union. */
function parseAssetContent(raw, where) {
  let parsed = raw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      throw new Error(`[${where}] JSON.parse failed: ${e?.message || e}`);
    }
  }

  if (Array.isArray(parsed)) {
    return looksHierarchical(parsed)
      ? normalizeOptionTree(parsed, where)
      : normalizeOptions(parsed, where);
  }

  if (parsed && typeof parsed === "object") {
    if (Array.isArray(parsed.options))
      return normalizeOptions(parsed.options, `${where} (options)`);
    if (Array.isArray(parsed.items))
      return normalizeOptions(parsed.items, `${where} (items)`);
    if (looksHierarchical(parsed)) return normalizeOptionTree(parsed, where);
  }

  throw new Error(
    `[${where}] content must be an array, {options|items}, or hierarchical {topics|subtopics|children}.`
  );
}

/* ----------------------------- STATE / CACHES ------------------------------ */

const state = {
  // Caches (normalized data), now **per-build only**:
  assetByFieldName: new Map(), // fieldName -> normalized {type:"flat"|"tree", ...}
  assetMissByFieldName: new Set(), // miss cache to avoid repeated lookups per build
  lastErrorByField: new Map(), // last error string per field
};

/* --------------------------------- API ------------------------------------ */

const FieldOptions = {
  /**
   * Idempotent init – signature kept for compatibility with existing callers.
   * Currently does not store anything; registry is the single source of truth.
   */
  init(_args = {}) {
    // no-op; we keep this to avoid breaking callers that pass { getAssetByKey }
  },

  clearCache() {
    // Full reset of current caches (used only by explicit callers).
    state.assetByFieldName = new Map();
    state.assetMissByFieldName = new Set();
    state.lastErrorByField = new Map();
  },

  /**
   * Build everything needed for a set of fields:
   *  - Validate inline options (no caching).
   *  - Prefetch all asset-backed lists by FIELD NAME (if AssetEntry exists).
   *
   * IMPORTANT:
   *   - Uses a per-build "nextState" and swaps it into `state` only after
   *     all assets have been fetched/normalized.
   *   - This avoids a window where caches are cleared but the UI is still
   *     rendering with the previous processed fields.
   */
  async buildFromFields({ fields } = {}) {
    if (!Array.isArray(fields)) {
      throw new Error("[FieldOptions.buildFromFields] 'fields' must be an array.");
    }

    const uniqueFieldNames = new Set();

    // Per-build local caches
    const nextState = {
      assetByFieldName: new Map(),
      assetMissByFieldName: new Set(),
      lastErrorByField: new Map(),
    };

    for (const f of fields) {
      const name = f?.field;
      if (typeof name === "string" && name.length) {
        uniqueFieldNames.add(name);
      }

      // Validate inline (if present) — diagnostics only, no cache writes here
      const inline = f?.options;
      if (inline !== undefined) {
        if (looksHierarchical(inline)) {
          normalizeOptionTree(inline, `field '${name}'.options`);
        } else if (Array.isArray(inline)) {
          normalizeOptions(inline, `field '${name}'.options`);
        } else if (
          Array.isArray(inline?.options) ||
          Array.isArray(inline?.items)
        ) {
          const arr = inline.options ?? inline.items;
          normalizeOptions(arr, `field '${name}'.options`);
        } else if (inline && typeof inline === "object") {
          normalizeOptionTree(inline, `field '${name}'.options`);
        }
      }
    }

    // Prefetch assets by field name (asset-first contract via AssetRegistry)
    for (const fieldName of uniqueFieldNames) {
      await this._ensureAssetForField(fieldName, nextState);
    }

    // Atomically swap caches once this build is fully ready
    state.assetByFieldName = nextState.assetByFieldName;
    state.assetMissByFieldName = nextState.assetMissByFieldName;
    state.lastErrorByField = nextState.lastErrorByField;
  },

  // Internal: ensure we have a cached normalized asset for a fieldName (if any)
  async _ensureAssetForField(fieldName, targetState = state) {
    if (!fieldName) return;

    const {
      assetByFieldName,
      assetMissByFieldName,
      lastErrorByField,
    } = targetState;

    // Short-circuit within a single build only
    if (
      assetByFieldName.has(fieldName) ||
      assetMissByFieldName.has(fieldName)
    ) {
      return;
    }

    // Look up AssetEntry from registry using fieldName as assetKey
    const assetEntry = getAssetEntryByKey(fieldName);
    if (!assetEntry) {
      assetMissByFieldName.add(fieldName);
      lastErrorByField.set(fieldName, "no AssetEntry for fieldName");
      return;
    }

    try {
      const content = await getAssetJson({ assetKey: fieldName });
      const normalized = parseAssetContent(
        content,
        `asset(field:'${fieldName}') ${assetEntry.s3Key || ""}`
      );
      assetByFieldName.set(fieldName, normalized);
    } catch (e) {
      assetMissByFieldName.add(fieldName);
      lastErrorByField.set(
        fieldName,
        `fetch/parse error: ${e?.message || e}`
      );
    }
  },

  /**
   * INTERNAL: Resolve to union payload (flat|tree) for a field.
   * Order: asset-by-fieldName -> inline options -> null
   * (This does not fetch; cache must be prebuilt via buildFromFields.)
   */
  _getForField({ fieldName = "?", options }) {
    // 1) Asset by field name (per-build normalized cache)
    const fromAsset = state.assetByFieldName.get(fieldName);
    if (fromAsset) {
      return cloneOptionData(fromAsset);
    }

    // 2) Inline fallback
    if (options !== undefined) {
      let normalized;
      if (looksHierarchical(options)) {
        normalized = normalizeOptionTree(
          options,
          `field '${fieldName}'.options`
        );
      } else if (Array.isArray(options)) {
        normalized = normalizeOptions(
          options,
          `field '${fieldName}'.options`
        );
      } else if (
        Array.isArray(options?.options) ||
        Array.isArray(options?.items)
      ) {
        const arr = options.options ?? options.items;
        normalized = normalizeOptions(
          arr,
          `field '${fieldName}'.options`
        );
      } else if (options && typeof options === "object") {
        // object without options/items but still a node → treat as tree
        normalized = normalizeOptionTree(
          options,
          `field '${fieldName}'.options`
        );
      } else {
        normalized = null;
      }

      if (normalized) {
        return cloneOptionData(normalized);
      }
    }

    return null;
  },

  /** Flat only. */
  resolveFlatOptions(args = {}) {
    const fieldName = args.fieldName || "?";
    const payload = this._getForField({
      fieldName,
      options: args.options,
    });

    return payload?.type === "flat"
      ? Array.isArray(payload.options)
        ? payload.options.map((o) => ({ name: o.name, value: o.value }))
        : []
      : null;
  },

  /** Tree only. */
  resolveTreeOptions(args = {}) {
    const fieldName = args.fieldName || "?";
    const payload = this._getForField({
      fieldName,
      options: args.options,
    });

    if (payload?.type !== "tree" || !Array.isArray(payload.tree)) {
      return null;
    }

    // defensive deep clone
    const cloneNode = (n) => ({
      name: n.name,
      value: n.value,
      ...(n.description ? { description: n.description } : {}),
      ...(Array.isArray(n.children)
        ? { children: n.children.map(cloneNode) }
        : {}),
    });

    return payload.tree.map(cloneNode);
  },
};

/**
 * Legacy helper kept exported for compatibility.
 * NOTE: This now uses AssetRegistry and requires asset.key to be defined.
 */
export async function fetchAssetFile(asset) {
  if (!asset || (typeof asset !== "object" && typeof asset !== "function")) {
    throw new Error("fetchAssetFile: invalid asset");
  }

  const assetKey =
    asset.key || asset.assetKey || asset.fieldName || null;

  if (!assetKey) {
    throw new Error(
      "fetchAssetFile: cannot resolve asset key; expected asset.key / asset.assetKey / asset.fieldName"
    );
  }

  return getAssetJson({ assetKey });
}

export default FieldOptions;
